export type AdvanceBookingDays = { min?: number; max?: number };

export type NormalizedAdvanceBookingDays = { min: number; max: number };

export const DEFAULT_ADVANCE_BOOKING_DAYS: NormalizedAdvanceBookingDays = { min: 0, max: 7 };

/** Default chip count when the allowed range is very long. */
export const ADVANCE_BOOKING_QUICK_PICK_COUNT = 14;

/** Show every day in the chip row when the range is at most this many days. */
export const ADVANCE_BOOKING_FULL_CHIP_RANGE = 90;

export function normalizeAdvanceBookingDays(
  value: AdvanceBookingDays | null | undefined,
  options?: { allowNegativeMin?: boolean },
): NormalizedAdvanceBookingDays {
  const min = value?.min ?? DEFAULT_ADVANCE_BOOKING_DAYS.min;
  const max = value?.max ?? DEFAULT_ADVANCE_BOOKING_DAYS.max;
  const normalizedMin = options?.allowNegativeMin ? min : Math.max(0, min);
  return { min: normalizedMin, max: Math.max(normalizedMin, max) };
}

/** Admin/staff booking form: staff window when configured, else resident window. */
/** Building service settings (thi công / ra vào / chuyển đồ). */
export function resolveServiceAdvanceBookingDays(
  settings: {
    advanceBookingDays?: AdvanceBookingDays | null;
    staffAdvanceBookingDays?: AdvanceBookingDays | null;
  } | null
  | undefined,
  userRole: string | undefined,
): NormalizedAdvanceBookingDays {
  const isStaff = userRole !== 'RESIDENT';
  if (
    isStaff &&
    settings?.staffAdvanceBookingDays &&
    (settings.staffAdvanceBookingDays.min !== undefined ||
      settings.staffAdvanceBookingDays.max !== undefined)
  ) {
    return normalizeAdvanceBookingDays(settings.staffAdvanceBookingDays, { allowNegativeMin: true });
  }
  if (
    settings?.advanceBookingDays &&
    (settings.advanceBookingDays.min !== undefined || settings.advanceBookingDays.max !== undefined)
  ) {
    return normalizeAdvanceBookingDays(settings.advanceBookingDays);
  }
  return normalizeAdvanceBookingDays(null, { allowNegativeMin: isStaff });
}

export function formatDatetimeLocalValue(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}-${m}-${d}T${h}:${min}`;
}

export function buildAdvanceBookingIsoDateBounds(bounds: NormalizedAdvanceBookingDays): {
  minDate: string;
  maxDate: string;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    minDate: formatLocalDateString(addDaysToDate(today, bounds.min)),
    maxDate: formatLocalDateString(addDaysToDate(today, bounds.max)),
  };
}

export function buildAdvanceBookingDatetimeLocalBounds(bounds: NormalizedAdvanceBookingDays): {
  min: string;
  max: string;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = addDaysToDate(today, bounds.min);
  const maxDate = addDaysToDate(today, bounds.max);
  maxDate.setHours(23, 59, 0, 0);
  return {
    min: formatDatetimeLocalValue(minDate),
    max: formatDatetimeLocalValue(maxDate),
  };
}

export function advanceBookingWindowError(
  isoDate: string,
  bounds: NormalizedAdvanceBookingDays,
  options?: { allowNegativeMin?: boolean },
): string | null {
  if (isDateWithinAdvanceBookingWindow(isoDate, bounds, options)) {
    return null;
  }
  return `Ngày đặt phải trong khoảng từ ${bounds.min} đến ${bounds.max} ngày kể từ hôm nay`;
}

export function resolveStaffAdvanceBookingDays(utility: {
  staffAdvanceBookingDays?: AdvanceBookingDays | null;
  advanceBookingDays?: AdvanceBookingDays | null;
}): NormalizedAdvanceBookingDays {
  if (
    utility.staffAdvanceBookingDays &&
    (utility.staffAdvanceBookingDays.min !== undefined ||
      utility.staffAdvanceBookingDays.max !== undefined)
  ) {
    return normalizeAdvanceBookingDays(utility.staffAdvanceBookingDays, { allowNegativeMin: true });
  }
  return normalizeAdvanceBookingDays(utility.advanceBookingDays);
}

export function parseAdvanceBookingMinInput(raw: string, fallback = 0): number {
  if (raw.trim() === '') return fallback;
  const parsed = parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function formatLocalDateString(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function addDaysToDate(base: Date, days: number): Date {
  const result = new Date(base);
  result.setHours(0, 0, 0, 0);
  result.setDate(result.getDate() + days);
  return result;
}

export type BookingDayChip = {
  date: string;
  dayName: string;
  dayNumber: number;
  month: string;
  isToday: boolean;
  isPast: boolean;
};

export function buildBookingDayChips(
  advanceBookingDays: AdvanceBookingDays | null | undefined,
  quickPickCount = ADVANCE_BOOKING_QUICK_PICK_COUNT,
  options?: { allowNegativeMin?: boolean },
): { chips: BookingDayChip[]; minDate: string; maxDate: string } {
  const { min, max } = normalizeAdvanceBookingDays(advanceBookingDays, options);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const minDate = formatLocalDateString(addDaysToDate(today, min));
  const maxDate = formatLocalDateString(addDaysToDate(today, max));

  const dayNames = ['CN', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7'];
  const monthNames = [
    'Th1', 'Th2', 'Th3', 'Th4', 'Th5', 'Th6', 'Th7', 'Th8', 'Th9', 'Th10', 'Th11', 'Th12',
  ];

  const chips: BookingDayChip[] = [];
  const rangeDays = max - min + 1;
  const endQuick =
    rangeDays <= ADVANCE_BOOKING_FULL_CHIP_RANGE
      ? max
      : Math.min(max, min + quickPickCount - 1);
  const todayStr = formatLocalDateString(today);

  for (let offset = min; offset <= endQuick; offset++) {
    const date = addDaysToDate(today, offset);
    const dateStr = formatLocalDateString(date);
    chips.push({
      date: dateStr,
      dayName: dayNames[date.getDay()],
      dayNumber: date.getDate(),
      month: monthNames[date.getMonth()],
      isToday: dateStr === todayStr,
      isPast: offset < 0,
    });
  }

  return { chips, minDate, maxDate };
}

export function daysOffsetFromToday(dateStr: string, baseDate = new Date()): number | null {
  const today = new Date(baseDate);
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function dateFromDaysOffset(offset: number, baseDate = new Date()): string {
  const today = new Date(baseDate);
  today.setHours(0, 0, 0, 0);
  return formatLocalDateString(addDaysToDate(today, offset));
}

/** Parse dd/mm/yyyy (or dd-mm-yyyy) to yyyy-mm-dd for the date input. */
export function parseDdMmYyyyToIsoDate(text: string): string | null {
  const trimmed = text.trim();
  const match = trimmed.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/);
  if (!match) return null;
  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return formatLocalDateString(date);
}

export function formatIsoDateToDdMmYyyy(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  if (!y || !m || !d) return '';
  return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
}

export function isDateWithinAdvanceBookingWindow(
  dateStr: string,
  advanceBookingDays: AdvanceBookingDays | null | undefined,
  options?: { allowNegativeMin?: boolean },
): boolean {
  const { min, max } = normalizeAdvanceBookingDays(advanceBookingDays, options);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(target.getTime())) return false;
  const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays >= min && diffDays <= max;
}

export function addDaysToIsoDate(startIso: string, dayOffset: number): string {
  const base = new Date(`${startIso}T12:00:00`);
  return formatLocalDateString(addDaysToDate(base, dayOffset));
}

export function enumerateIsoDateRange(startIso: string, endIso: string): string[] {
  const start = new Date(`${startIso}T12:00:00`);
  const end = new Date(`${endIso}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
    return [startIso];
  }
  const dates: string[] = [];
  for (let cur = new Date(start); cur.getTime() <= end.getTime(); cur.setDate(cur.getDate() + 1)) {
    dates.push(formatLocalDateString(cur));
  }
  return dates;
}

export function countInclusiveDays(startIso: string, endIso: string): number {
  return enumerateIsoDateRange(startIso, endIso).length;
}
